import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/header";
import {
  HELP_RAIL_STORAGE_KEY,
  HELP_TOPICS,
  articlesForTopic,
  contactHref,
  findArticle,
  searchHelp,
} from "../help/library";
import "./Help.css";

function StepLine({ text }) {
  const linked = text.match(/^\*\*\[([^\]]+)\]\((https:\/\/[^)\s]+|\/[^)\s]*)\)\*\*\s*([\s\S]*)$/);
  if (linked) {
    const href = linked[2];
    const onThisSite = href.startsWith("/");
    return (
      <>
        <strong className="help-step-name">
          {onThisSite ? (
            <Link to={href}>{linked[1]}</Link>
          ) : (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {linked[1]}
            </a>
          )}
        </strong>
        {linked[3] ? <span>{linked[3]}</span> : null}
      </>
    );
  }
  const labeled = text.match(/^\*\*([^*]+)\*\*\s*([\s\S]*)$/);
  if (!labeled) return text;
  return (
    <>
      <strong className="help-step-name">{labeled[1]}</strong>
      {labeled[2] ? <span>{labeled[2]}</span> : null}
    </>
  );
}

function readRailMode() {
  try {
    const value = localStorage.getItem(HELP_RAIL_STORAGE_KEY);
    if (value === "light" || value === "dark") return value;
  } catch {
    /* ignore */
  }
  return "dark";
}

const Help = () => {
  const navigate = useNavigate();
  const [topicId, setTopicId] = useState(HELP_TOPICS[0].id);
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);
  const [railMode, setRailMode] = useState(readRailMode);

  const hits = searchHelp(query);
  const tiles = hits || articlesForTopic(topicId);
  const topic = HELP_TOPICS.find((item) => item.id === topicId) || HELP_TOPICS[0];
  const open = openId ? findArticle(openId) : null;

  useEffect(() => {
    try {
      localStorage.setItem(HELP_RAIL_STORAGE_KEY, railMode);
    } catch {
      /* ignore */
    }
  }, [railMode]);

  useEffect(() => {
    if (!openId) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setOpenId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openId]);

  const openArticle = (article) => setOpenId(article.id);

  return (
    <div className="help-page">
      <Header />
      <div className="help-shell">
        <aside className={`help-rail help-rail--${railMode}`} aria-label="Help topics">
          <p className="help-rail-label">Help</p>
          {HELP_TOPICS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`help-topic${item.id === topicId ? " is-active" : ""}`}
              aria-current={item.id === topicId ? "true" : undefined}
              onClick={() => {
                setTopicId(item.id);
                setQuery("");
              }}
            >
              {item.title}
            </button>
          ))}
          <button
            type="button"
            className="help-rail-toggle"
            onClick={() => setRailMode((mode) => (mode === "dark" ? "light" : "dark"))}
          >
            {railMode === "dark" ? "White menu" : "Black menu"}
          </button>
        </aside>
        <main className="help-main">
          <input
            className="help-search"
            type="search"
            value={query}
            placeholder="Search help"
            aria-label="Search help"
            onChange={(event) => setQuery(event.target.value)}
          />
          <h1 className="help-heading">{hits ? "Search" : topic.title}</h1>
          <p className="help-count">
            {hits
              ? `${hits.length} ${hits.length === 1 ? "article" : "articles"}`
              : "Choose a topic. A tile opens the steps."}
          </p>
          {tiles.length === 0 ? (
            <p className="help-empty">No articles contain those words.</p>
          ) : (
            <div className="help-grid">
              {tiles.map(({ article }) => (
                <button
                  key={article.id}
                  type="button"
                  className="help-tile"
                  onClick={() => openArticle(article)}
                >
                  <h2>{article.title}</h2>
                  <p>{article.summary}</p>
                </button>
              ))}
            </div>
          )}
        </main>
      </div>
      {open ? (
        <div className="help-modal-backdrop" onClick={() => setOpenId(null)}>
          <div
            className="help-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="help-article-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="help-modal-top">
              <button type="button" className="help-close" onClick={() => setOpenId(null)}>
                Close
              </button>
            </div>
            <p className="help-kicker">{open.topic.title}</p>
            <h2 id="help-article-title">{open.article.title}</h2>
            {open.article.pending ? (
              <p className="help-pending">The steps for this topic are still being written.</p>
            ) : (
              <ol>
                {open.article.steps.map((step) => (
                  <li key={step}>
                    <StepLine text={step} />
                  </li>
                ))}
              </ol>
            )}
            <div className="help-modal-actions">
              {open.article.relatedId ? (
                <button
                  type="button"
                  className="help-related"
                  onClick={() => setOpenId(open.article.relatedId)}
                >
                  {open.article.relatedLabel} →
                </button>
              ) : null}
              <button
                type="button"
                className="help-ask"
                onClick={() => navigate(contactHref(open.article.title))}
              >
                Ask a question
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Help;
